import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import type { StreamingInsightsPayload } from "@/types/marketing";
import { ChartsLoader } from "@/components/charts/ChartsLoader";

const PLATFORM_LABELS: Record<string, string> = {
  spotify: "Spotify",
  youtubeMusic: "YouTube Music",
  appleMusic: "Apple Music",
};

const PLATFORM_COLORS: Record<string, string> = {
  spotify: "#22c55e",
  youtubeMusic: "#ef4444",
  appleMusic: "#f97316",
};

const formatNumber = (value: number) => {
  if (!Number.isFinite(value)) return "0";
  if (Math.abs(value) >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1)}m`;
  }
  if (Math.abs(value) >= 1_000) {
    return `${(value / 1_000).toFixed(1)}k`;
  }
  return value.toFixed(0);
};

type StreamingInsightsPanelProps = {
  data: StreamingInsightsPayload | null;
  loading?: boolean;
};

export function StreamingInsightsPanel({ data, loading }: StreamingInsightsPanelProps) {
  const timeline = data?.timeline ?? [];
  const comparison = data?.platformComparison ?? [];
  const anomalies = data?.anomalies ?? [];
  const correlations = data?.correlations ?? [];
  const sources = data?.sources ?? {};

  return (
    <Card className="space-y-4 border-border/70 bg-background/40 p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold text-foreground">Streaming Insights</p>
          <p className="text-xs text-muted-foreground">Platforms trends vs publishing cadence.</p>
        </div>
      </div>
      {loading ? (
        <p className="text-sm text-muted-foreground">Loading streaming analytics…</p>
      ) : (
        <div className="space-y-4">
          {Object.keys(sources).length ? (
            <div className="grid gap-3 md:grid-cols-3">
              {Object.entries(sources).map(([platform, snapshot]) => (
                <div key={platform} className="rounded-xl border border-border/60 bg-background/40 p-3">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">{PLATFORM_LABELS[platform] ?? platform}</p>
                  <p className="mt-1 text-2xl font-semibold text-foreground">{formatNumber(snapshot.current)}</p>
                  <p className="text-xs text-muted-foreground">
                    <span className={snapshot.delta >= 0 ? "text-emerald-300" : "text-rose-300"}>
                      {snapshot.delta >= 0 ? "+" : ""}
                      {formatNumber(snapshot.delta)}
                    </span>
                    {" "}
                    ({(snapshot.percentChange * 100).toFixed(1)}%) vs prev
                  </p>
                </div>
              ))}
            </div>
          ) : null}

          <section>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Streams vs Posts</p>
            {timeline.length === 0 ? (
              <p className="mt-2 text-sm text-muted-foreground">No streaming history yet.</p>
            ) : (
              <div className="mt-3 h-64">
                <ChartsLoader
                  fallback={<p className="text-xs text-muted-foreground">Loading chart…</p>}
                  errorFallback={<p className="text-xs text-muted-foreground">Chart unavailable.</p>}
                >
                  {({ ResponsiveContainer, ComposedChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, Legend, Bar }) => (
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={timeline} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                        <XAxis dataKey="date" tick={{ fill: "#94a3b8", fontSize: 12 }} stroke="#1e293b" />
                        <YAxis yAxisId="left" tick={{ fill: "#94a3b8", fontSize: 12 }} stroke="#1e293b" />
                        <YAxis yAxisId="right" orientation="right" tick={{ fill: "#94a3b8", fontSize: 12 }} stroke="#1e293b" />
                        <Tooltip contentStyle={{ backgroundColor: "#020617", borderColor: "#1e293b" }} />
                        <Legend />
                        <Bar yAxisId="right" dataKey="posts" fill="#475569" opacity={0.4} name="Posts" />
                        <Line
                          yAxisId="left"
                          type="monotone"
                          dataKey="spotify"
                          name="Spotify"
                          stroke={PLATFORM_COLORS.spotify}
                          strokeWidth={2}
                          dot={false}
                        />
                        <Line
                          yAxisId="left"
                          type="monotone"
                          dataKey="youtubeMusic"
                          name="YouTube Music"
                          stroke={PLATFORM_COLORS.youtubeMusic}
                          strokeWidth={2}
                          dot={false}
                        />
                        <Line
                          yAxisId="left"
                          type="monotone"
                          dataKey="appleMusic"
                          name="Apple Music"
                          stroke={PLATFORM_COLORS.appleMusic}
                          strokeWidth={2}
                          dot={false}
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  )}
                </ChartsLoader>
              </div>
            )}
          </section>

          <section className="grid gap-4 lg:grid-cols-2">
            <div className="rounded-xl border border-border/60 bg-background/40 p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Spike detection</p>
              {anomalies.length === 0 ? (
                <p className="mt-2 text-sm text-muted-foreground">No spikes detected in the selected window.</p>
              ) : (
                <div className="mt-3 space-y-2">
                  {anomalies.slice(0, 6).map((anomaly) => (
                    <div key={`${anomaly.platform}-${anomaly.date}`} className="flex items-center justify-between rounded-lg border border-border/60 bg-background/30 px-3 py-2">
                      <div>
                        <p className="text-sm font-semibold text-foreground">{PLATFORM_LABELS[anomaly.platform] ?? anomaly.platform}</p>
                        <p className="text-xs text-muted-foreground">{anomaly.date}</p>
                      </div>
                      <Badge variant="outline" className={anomaly.direction === "up" ? "border-emerald-400/40 text-emerald-200" : "border-rose-400/40 text-rose-300"}>
                        {anomaly.direction === "up" ? "Spike" : "Drop"}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-xl border border-border/60 bg-background/40 p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Platform comparison</p>
              {comparison.length === 0 ? (
                <p className="mt-2 text-sm text-muted-foreground">No comparable data.</p>
              ) : (
                <div className="mt-3 h-56">
                  <ChartsLoader
                    fallback={<p className="text-xs text-muted-foreground">Loading chart…</p>}
                    errorFallback={<p className="text-xs text-muted-foreground">Chart unavailable.</p>}
                  >
                    {({ ResponsiveContainer, BarChart, CartesianGrid, XAxis, YAxis, Tooltip, Bar, Cell }) => (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={comparison} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                          <XAxis dataKey="platform" tickFormatter={(value) => PLATFORM_LABELS[value] ?? value} tick={{ fill: "#94a3b8", fontSize: 12 }} stroke="#1e293b" />
                          <YAxis tick={{ fill: "#94a3b8", fontSize: 12 }} stroke="#1e293b" />
                          <Tooltip
                            formatter={(value: number, platformKey) => [formatNumber(value), PLATFORM_LABELS[String(platformKey)] ?? platformKey]}
                            contentStyle={{ backgroundColor: "#020617", borderColor: "#1e293b" }}
                          />
                          <Bar dataKey="total" radius={[4, 4, 0, 0]}>
                            {comparison.map((entry) => (
                              <Cell key={entry.platform} fill={PLATFORM_COLORS[entry.platform] ?? "#38bdf8"} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </ChartsLoader>
                </div>
              )}
            </div>
          </section>

          <section>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Correlation vs posting</p>
            {correlations.length === 0 ? (
              <p className="mt-2 text-sm text-muted-foreground">Not enough data to compute correlations.</p>
            ) : (
              <div className="mt-2 flex flex-wrap gap-3">
                {correlations.map((entry) => (
                  <div key={entry.platform} className="rounded-xl border border-border/60 bg-background/40 px-4 py-2">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">{PLATFORM_LABELS[entry.platform] ?? entry.platform}</p>
                    <p className="text-sm font-semibold text-foreground">{entry.coefficient.toFixed(2)}</p>
                    <p className="text-[10px] text-muted-foreground">Pearson r</p>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      )}
    </Card>
  );
}
